import { useState, useEffect, useRef } from "react";
import { Building2, Map, Star, Globe, Signal } from "lucide-react";

/* ── Color helpers ── */
function scoreColor(s) {
  if (s >= 86) return "#22c55e";
  if (s >= 66) return "#eab308";
  if (s >= 41) return "#f97316";
  return "#ef4444";
}

function scoreBadge(s) {
  if (s >= 86) return { label: "GREAT", bg: "#dcfce7", color: "#166534" };
  if (s >= 66) return { label: "GOOD", bg: "#fef9c3", color: "#854d0e" };
  if (s >= 41) return { label: "FAIR", bg: "#ffedd5", color: "#9a3412" };
  return { label: "POOR", bg: "#fee2e2", color: "#991b1b" };
}

/* ── Animated counter ── */
function AnimCount({ value, duration = 800 }) {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);
  useEffect(() => {
    const from = prevRef.current;
    prevRef.current = value;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      setDisplay(Math.round(from + (value - from) * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{display}</>;
}

/* ── Sub-score card ── */
function SubScoreCard({ icon: Icon, label, score, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  const [barWidth, setBarWidth] = useState(0);
  const badge = scoreBadge(score);
  const color = scoreColor(score);

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), delay);
    const t2 = setTimeout(() => setBarWidth(score), delay + 200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [score, delay]);

  return (
    <div
      data-testid={`sub-score-${label.toLowerCase().replace(/\s/g, "-")}`}
      style={{
        background: "#fff", borderRadius: 12,
        border: "1px solid #e5e7eb", borderTop: `3px solid ${color}`,
        padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(12px)",
        transition: "opacity 0.4s ease, transform 0.4s ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <Icon size={16} style={{ color: "#6b7280" }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>{label}</span>
      </div>
      <div style={{ fontSize: 36, fontWeight: 900, color, lineHeight: 1, marginBottom: 10 }}>
        <AnimCount value={score} duration={800 + delay} />
      </div>
      <div style={{ height: 6, borderRadius: 3, background: "#e5e7eb", overflow: "hidden", marginBottom: 10 }}>
        <div style={{ height: "100%", borderRadius: 3, background: color, width: `${barWidth}%`, transition: "width 0.8s ease-out" }} />
      </div>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", padding: "3px 10px", borderRadius: 4, background: badge.bg, color: badge.color }}>
        {badge.label}
      </span>
    </div>
  );
}


/* ══════════════════════════════════════════════════
   Sub-score calculation functions (user-specified formulas)
   ══════════════════════════════════════════════════ */

function calcGbpScore(business, scores) {
  const biz = business || {};
  const gbpDetails = (scores?.gbp?.details) || [];
  const signals = scores?.signals || [];
  const postSignal = signals.find(s => s.key === "posts");
  const responseSignal = signals.find(s => s.key === "review_response");

  // Check each field
  const hasDescription = biz.description ? 100 : 0;

  const photoCount = biz.photos?.length || 0;
  const hasPhotos = photoCount >= 10 ? 100 : photoCount >= 5 ? 60 : 20;

  const hoursDetail = gbpDetails.find(d => d.field === "Hours");
  const hasHours = hoursDetail?.status === "confirmed_present" ? 100 : 0;

  const hasWebsite = biz.website ? 100 : 0;

  const catCount = biz.categories?.length || 0;
  const hasCategories = catCount >= 2 ? 100 : 50;

  // is_verified: if GBP has confirmed_present fields, likely claimed
  const confirmedCount = gbpDetails.filter(d => d.status === "confirmed_present").length;
  const isClaimed = confirmedCount >= 3 ? 100 : 0;

  // has_posts: from posts signal score (0 = no posts, 20 = infrequent, etc.)
  const hasPosts = postSignal ? (postSignal.score > 50 ? 100 : 0) : 0;

  // response_to_reviews: derive reply rate from signal score
  const replyRate = responseSignal ? responseSignal.score / 100 : 0;
  const responseScore = replyRate >= 0.5 ? 100 : Math.round(replyRate * 200);

  const checks = [hasDescription, hasPhotos, hasHours, hasWebsite, hasCategories, isClaimed, hasPosts, responseScore];
  return Math.round(checks.reduce((a, b) => a + b, 0) / checks.length);
}

function calcRankingsScore(geo_grid) {
  const geoKeys = Object.keys(geo_grid || {}).filter(k => k !== "__meta__");
  if (geoKeys.length === 0) return 5;

  function rankToScore(avgRank) {
    const r = Math.round(avgRank);
    if (r <= 1) return 100;
    if (r <= 2) return 90;
    if (r <= 3) return 80;
    if (r <= 5) return 65;
    if (r <= 7) return 45;
    if (r <= 10) return 30;
    if (r <= 20) return 15;
    return 5;
  }

  const kwScores = geoKeys.map(kw => {
    const data = geo_grid[kw];
    const avg = data?.average_rank;
    if (avg == null) return 5;
    return rankToScore(avg);
  });

  return Math.round(kwScores.reduce((a, b) => a + b, 0) / kwScores.length);
}

function calcReputationScore(business, scores) {
  const biz = business || {};
  const rating = biz.rating || 0;
  const reviewCount = biz.review_count || 0;
  const responseSignal = (scores?.signals || []).find(s => s.key === "review_response");
  const velocitySignal = (scores?.signals || []).find(s => s.key === "review_velocity");

  // rating_score
  const ratingScore = (rating / 5) * 100;

  // review_count_score
  let countScore = 10;
  if (reviewCount >= 500) countScore = 100;
  else if (reviewCount >= 200) countScore = 85;
  else if (reviewCount >= 100) countScore = 70;
  else if (reviewCount >= 50) countScore = 55;
  else if (reviewCount >= 20) countScore = 40;
  else if (reviewCount >= 10) countScore = 25;

  // response_rate_score: derive from signal (score 60 → ~60% reply)
  const replyPct = responseSignal ? responseSignal.score : 0;
  let responseScore = 10;
  if (replyPct >= 80) responseScore = 100;
  else if (replyPct >= 50) responseScore = 70;
  else if (replyPct >= 20) responseScore = 40;

  // recency_score: derive from velocity signal (100 = very active, 0 = stale)
  const velScore = velocitySignal ? velocitySignal.score : 20;
  let recencyScore = 20;
  if (velScore >= 90) recencyScore = 100;
  else if (velScore >= 60) recencyScore = 80;
  else if (velScore >= 30) recencyScore = 50;

  return Math.round(
    ratingScore * 0.35 +
    countScore * 0.25 +
    responseScore * 0.25 +
    recencyScore * 0.15
  );
}

function calcWebsiteScore(wa) {
  if (!wa) return 0;

  const hasSsl = wa.has_https ? 100 : 0;
  const title = wa.title || "";
  const hasMetaTitle = title ? (title.length <= 60 ? 100 : 50) : 0;
  const hasMetaDesc = wa.meta_description ? 100 : 0;
  const hasH1 = (wa.h1_tags?.length || 0) > 0 ? 100 : 0;
  const hasSchema = wa.has_schema ? 100 : 0;
  const pagespeed = wa.pagespeed_mobile ?? 50;
  const napScore = wa.nap_score ?? 0;
  const mobileFriendly = wa.mobile_friendly ?? 50;

  const checks = [hasSsl, hasMetaTitle, hasMetaDesc, hasH1, hasSchema, pagespeed, napScore, mobileFriendly];
  return Math.round(checks.reduce((a, b) => a + b, 0) / checks.length);
}

function calcCitationsScore(scores) {
  const signals = scores?.signals || [];
  const citationSig = signals.find(s => s.key === "citations");
  const backlinkSig = signals.find(s => s.key === "backlinks");
  const postsSig = signals.find(s => s.key === "posts");
  const gpsSig = signals.find(s => s.key === "gps");
  const aiVis = scores?.ai_readiness?.score;

  const citationScore = citationSig?.score ?? 0;
  const backlinkScore = backlinkSig?.score ?? 0;
  const postsScore = postsSig ? (postsSig.score > 50 ? 80 : 10) : 10;
  const socialScore = gpsSig?.score ?? 0;
  const aiScore = (aiVis != null && aiVis > 50) ? 100 : 20;

  const checks = [citationScore, backlinkScore, postsScore, socialScore, aiScore];
  return Math.round(checks.reduce((a, b) => a + b, 0) / checks.length);
}


/* ═══ Main Component ═══ */
export default function DonutSubScores({ scores, website_analysis, geo_grid, business }) {
  const gbpScore = calcGbpScore(business, scores);
  const rankingsScore = calcRankingsScore(geo_grid);
  const reputationScore = calcReputationScore(business, scores);
  const websiteScore = calcWebsiteScore(website_analysis);
  const citationsScore = calcCitationsScore(scores);

  const subScores = [
    { icon: Building2, label: "GBP Profile", score: gbpScore },
    { icon: Map, label: "Rankings", score: rankingsScore },
    { icon: Star, label: "Reputation", score: reputationScore },
    { icon: Globe, label: "Website", score: websiteScore },
    { icon: Signal, label: "Citations", score: citationsScore },
  ];

  return (
    <section data-testid="donut-sub-scores" style={{ marginBottom: 32 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 10 }}>
        {subScores.map((s, i) => (
          <SubScoreCard key={s.label} icon={s.icon} label={s.label} score={s.score} delay={i * 100} />
        ))}
      </div>
    </section>
  );
}
